import React, { useEffect, useRef } from 'react';
import Flatpickr from 'react-flatpickr';
// Base CSS — the airbnb theme reads cleaner on the white surface; we tint a
// few specific selectors below so it matches our primary colour & dark mode.
import 'flatpickr/dist/themes/airbnb.css';
import './DateInput.css';
import { CalendarBlank, X } from '@phosphor-icons/react';

/**
 * App-wide date input wrapped around flatpickr.
 *
 * Why a shared component:
 *   - The native <input type="date"> picker has zero design parity across
 *     Chrome/Firefox/Safari (the user flagged this — the rendered UI doesn't
 *     match the rest of our forms).
 *   - flatpickr gives us a single calendar UI everywhere, and we can theme it
 *     once in DateInput.css to inherit our primary colour / dark-mode variables.
 *
 * Props mirror native <input type="date"> so existing call-sites are a 1:1
 * swap: { value: 'YYYY-MM-DD' | '', onChange: (newYYYYMMDD) => void }.
 *
 * The wrapper renders a small calendar icon + an inline "clear" button when a
 * value is set, mirroring the pattern used by the rest of the app's pills
 * (e.g. ImageryDatePill, RecipientsPanel).
 */
export function DateInput({
  value,
  onChange,
  className = '',
  placeholder = 'Pick a date',
  disabled = false,
  minDate,
  maxDate,
  testId,
}) {
  const ref = useRef(null);

  // Cleanup the flatpickr instance when the component unmounts. The react-
  // flatpickr wrapper handles most of this, but we also detach our manual
  // outside-click listener (added below for the clear button).
  useEffect(() => () => {
    try { ref.current?.flatpickr?.destroy?.(); } catch { /* already destroyed */ }
  }, []);

  return (
    <div className={`relative inline-block ${className}`} data-testid={testId}>
      <CalendarBlank
        size={13}
        weight="bold"
        className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
      />
      <Flatpickr
        ref={ref}
        value={value || ''}
        options={{
          dateFormat: 'Y-m-d',
          altInput: true,
          altFormat: 'd M Y',
          allowInput: true,
          disableMobile: false,   // use the same picker on mobile for consistency
          // Keep the original Month ▾ + Year input on top of the calendar
          // (default monthSelectorType is 'dropdown') — width overrides in
          // DateInput.css ensure the dropdown still fits the compact popup.
          static: false,
          minDate: minDate || undefined,
          maxDate: maxDate || undefined,
        }}
        onChange={(dates, str) => {
          // Always emit the YYYY-MM-DD form so call-sites that previously
          // expected `e.target.value` from a native date input keep working.
          onChange?.(str || '');
        }}
        disabled={disabled}
        placeholder={placeholder}
        className="app-date-input pl-7 pr-7 py-1.5 text-xs rounded-md bg-background border border-border text-foreground font-medium placeholder:text-muted-foreground placeholder:font-normal disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            // Prevent the click from re-opening the calendar that closes on blur.
            e.stopPropagation();
            onChange?.('');
            try { ref.current?.flatpickr?.clear?.(); } catch { /* noop */ }
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Clear date"
          data-testid={testId ? `${testId}-clear` : undefined}
          aria-label="Clear date"
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}
